import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import TriageLayout from "./TriageLayout";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import {
  UserPlus, ClipboardText, HeartStraight, ArrowsClockwise, FloppyDisk, Eraser, Trash
} from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { isSupervisorOrAbove } from "@/lib/permissions";
import { todayIsoDateET, nowHHMMET } from "@/lib/etTime";

/**
 * BSVAC Event Triage — new / edit patient care report.
 *
 * Mirrors the field layout at forms.osmcops.com. All backend fields are
 * optional except first/last name (frontend enforces those before submit).
 * A blank record can be saved as a draft ("Save Keep Open") or finalized
 * ("Submit & Close"). Provider signature is captured on a canvas and
 * stored as a PNG data URL.
 */
const REASONS = ["Medical", "Trauma", "Drug Usage", "ETOH", "Sick", "Chest Pains", "Unconscious"];
const TAG_COLORS = [
  { key: "green",  label: "Green (Minor)",     bg: "bg-emerald-500" },
  { key: "yellow", label: "Yellow (Delayed)",  bg: "bg-yellow-400 text-slate-900" },
  { key: "orange", label: "Orange (Urgent)",   bg: "bg-orange-500" },
  { key: "red",    label: "Red (Immediate)",   bg: "bg-red-600" },
  { key: "black",  label: "Black (Deceased)",  bg: "bg-slate-900" },
];
const PMH_CHECKS = ["None", "Unable to Obtain", "Asthma", "Diabetes", "Cardiac", "Hypertension", "COPD", "Emphysema", "Psychiatric"];
const CTC_OPTIONS = ["Cool", "Clammy", "Warm", "Dry", "Normal"];
const SIG_STATUS = ["Patient Signed", "Patient Refused to Sign", "Patient Unable to Sign", "Minor (Parent/Guardian Signs)"];

const EMPTY_VITALS = { time: "", pulse: "", bp: "", respiration: "", o2: "", ctc: [], bgl: "", avpu: "" };

function todayIsoDate() {
  // Anchored to Eastern Time so form_date matches the ACR paperwork and
  // the auto-generated PCR number, regardless of the operator's device
  // timezone.
  return todayIsoDateET();
}

export default function TriagePatientForm() {
  const nav = useNavigate();
  const { patientId } = useParams();
  const isEdit = Boolean(patientId);
  const { user } = useAuth();
  const canDelete = isEdit && isSupervisorOrAbove(user);
  const [form, setForm] = useState(() => ({
    form_type: "ACR",
    pcr_number: "",
    first_name: "", last_name: "", date_of_birth: "", gender: "", address: "", telephone: "",
    form_date: todayIsoDate(), event_id: "", event_name: "", triage_center: "", patient_contact_time: nowHHMMET(),
    reason_for_treatment: "", triage_tag_color: "",
    allergies: "", allergies_flags: { nka: false, unable: false },
    current_medication: "", meds_flags: { none: false, unable: false },
    past_medical_history: [], other_medical_history: "",
    vitals_na: false,
    vitals: [ {...EMPTY_VITALS}, {...EMPTY_VITALS}, {...EMPTY_VITALS}, {...EMPTY_VITALS} ],
    physical_assessment: "",
    being_transported: null, transported_by: "",
    hipaa_signature_status: "", hipaa_signature_date: todayIsoDate(),
    hipaa_signature_data_url: "",
    rma_signature_status: "", rma_signature_date: todayIsoDate(),
    rma_signature_data_url: "",
    provider_name: "", provider_certification: "",
    provider_signature_data_url: "",
    // OTC (Over-the-Counter) medication form. Multi-select checkboxes
    // — a single dose event may involve several meds (e.g. Tylenol +
    // Benadryl for pain + allergy). The blank text captures dose,
    // route, time, and anything not covered by the four presets.
    otc_meds: { tylenol: false, motrin: false, benadryl: false, antacids: false },
    otc_notes: "",
    // Patient-signed OTC disclaimer — separate from Provider Signature
    // because the patient is attesting to the acknowledgment terms
    // (allergies disclosed, voluntary use, hold-harmless language).
    otc_disclaimer_signature_data_url: "",
    otc_disclaimer_signature_date: todayIsoDate(),
    status: "submitted",
  }));
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(isEdit);
  const [events, setEvents] = useState([]);
  const sigPadRef = useRef(null);

  // Fetch events for the picker. Any triage user can list them.
  // In edit mode we also include past events so a report that was
  // initiated during a now-ended event can still show/keep its original
  // event selection. New patients only see active events so nobody
  // accidentally files a fresh ACR under a finished event.
  useEffect(() => {
    const url = isEdit ? "/triage/events?include_past=true" : "/triage/events";
    api.get(url)
      .then(({ data }) => setEvents(data?.events || []))
      .catch(() => setEvents([]));
  }, [isEdit]);

  // The currently-selected event, used to drive the Triage Center dropdown.
  const selectedEvent = events.find((e) => e.id === form.event_id) || null;

  // Load existing record when editing so provider can pick up where they left off.
  useEffect(() => {
    if (!isEdit) return;
    let cancelled = false;
    api.get(`/triage/patients/${patientId}`).then(({ data }) => {
      if (cancelled) return;
      // API returns nullable strings for optional fields. Coerce nulls to
      // empty strings so `.length` checks in the progress/validation logic
      // don't blow up, and so the flag-reverse-engineering below matches.
      const STR_FIELDS = [
        "pcr_number", "transported_by",
        "first_name", "last_name", "date_of_birth", "gender", "address", "telephone",
        "form_date", "event_name", "triage_center", "patient_contact_time",
        "reason_for_treatment", "triage_tag_color", "allergies", "current_medication",
        "other_medical_history", "physical_assessment",
        "hipaa_signature_status", "hipaa_signature_date", "hipaa_signature_data_url",
        "rma_signature_status", "rma_signature_date", "rma_signature_data_url",
        "provider_name", "provider_certification", "provider_signature_data_url",
      ];
      const clean = { ...data };
      for (const k of STR_FIELDS) if (clean[k] == null) clean[k] = "";
      // Reverse-engineer flag booleans from the flattened backend strings so
      // the "No Known Allergies" / "Unable to Obtain" chips reflect the
      // saved state instead of putting the literal phrase back into the
      // free-text field.
      const allergyRaw = clean.allergies || "";
      const allergyFlags = {
        nka: allergyRaw === "No Known Allergies",
        unable: allergyRaw === "Unable to Obtain",
      };
      const medsRaw = clean.current_medication || "";
      const medsFlags = {
        none: medsRaw === "No Current Medication",
        unable: medsRaw === "Unable to Obtain",
      };
      const vitalsArr = Array.isArray(clean.vitals) && clean.vitals.length
        ? [...clean.vitals, ...Array(Math.max(0, 4 - clean.vitals.length)).fill(0).map(() => ({ ...EMPTY_VITALS }))].slice(0, 4)
        : [{ ...EMPTY_VITALS }, { ...EMPTY_VITALS }, { ...EMPTY_VITALS }, { ...EMPTY_VITALS }];
      // Legacy records stored `ctc` as a single string. Coerce to array so
      // the multi-select renders correctly and future saves keep the new
      // shape. Also coerce missing notes.
      vitalsArr.forEach((v) => {
        if (typeof v.ctc === "string") v.ctc = v.ctc ? [v.ctc] : [];
        if (!Array.isArray(v.ctc)) v.ctc = [];
        // Legacy: prefer bgl, fall back to old notes field, coerce nulls.
        if (v.bgl == null) v.bgl = v.notes || "";
        delete v.notes;
        // AVPU is per-vital-set: single letter "A"|"V"|"P"|"U" or "".
        // Coerce legacy shapes (missing / array-typed) into a scalar.
        if (Array.isArray(v.avpu)) v.avpu = v.avpu[0] || "";
        if (!["A", "V", "P", "U"].includes(v.avpu)) v.avpu = "";
      });
      setForm((prev) => ({
        ...prev,
        ...clean,
        allergies: allergyFlags.nka || allergyFlags.unable ? "" : allergyRaw,
        allergies_flags: allergyFlags,
        current_medication: medsFlags.none || medsFlags.unable ? "" : medsRaw,
        meds_flags: medsFlags,
        past_medical_history: clean.past_medical_history || [],
        vitals: vitalsArr,
        vitals_na: !!clean.vitals_na,
        being_transported: clean.being_transported ?? null,
        provider_signature_data_url: clean.provider_signature_data_url || "",
        otc_meds: {
          tylenol: !!(clean.otc_meds && clean.otc_meds.tylenol),
          motrin: !!(clean.otc_meds && clean.otc_meds.motrin),
          benadryl: !!(clean.otc_meds && clean.otc_meds.benadryl),
          antacids: !!(clean.otc_meds && clean.otc_meds.antacids),
        },
        otc_notes: clean.otc_notes || "",
        otc_disclaimer_signature_data_url: clean.otc_disclaimer_signature_data_url || "",
        otc_disclaimer_signature_date: clean.otc_disclaimer_signature_date || todayIsoDate(),
      }));
      setLoading(false);
    }).catch((err) => {
      toast.error(err.response?.data?.detail || "Could not load record");
      nav("/triage/patients");
    });
    return () => { cancelled = true; };
  }, [isEdit, patientId, nav]);

  // Completion progress — enumerated as named checks so the operator can
  // pop the progress bar open and see exactly what's still missing before
  // Submit & Close will accept the record.
  const checklist = useMemo(() => {
    const notEmpty = (v) => typeof v === "string" ? v.trim().length > 0 : !!v;
    // OTC forms only capture demographics + allergies + medication
    // given. Everything else on the full ACR (vitals, PMH, HIPAA,
    // Treat & Release, transport) is skipped so the checklist stays
    // meaningful and Submit & Close isn't gated by fields the OTC UI
    // never renders.
    if (form.form_type === "OTC") {
      const otcMeds = form.otc_meds || {};
      return [
        { label: "Patient first name", ok: notEmpty(form.first_name) },
        { label: "Patient last name", ok: notEmpty(form.last_name) },
        { label: "Date of birth", ok: notEmpty(form.date_of_birth) },
        { label: "Telephone", ok: notEmpty(form.telephone) },
        { label: "Allergies (or NKA / Unable to Obtain)", ok: !!(notEmpty(form.allergies) || form.allergies_flags.nka || form.allergies_flags.unable) },
        { label: "Medication administered (tick at least one, or type notes)", ok: !!(otcMeds.tylenol || otcMeds.motrin || otcMeds.benadryl || otcMeds.antacids || notEmpty(form.otc_notes)) },
        { label: "Patient OTC disclaimer signature", ok: notEmpty(form.otc_disclaimer_signature_data_url) },
        { label: "Provider name", ok: notEmpty(form.provider_name) },
        { label: "Provider signature", ok: notEmpty(form.provider_signature_data_url) },
      ];
    }
    return [
      { label: "Patient first name", ok: notEmpty(form.first_name) },
      { label: "Patient last name", ok: notEmpty(form.last_name) },
      { label: "Date of birth", ok: notEmpty(form.date_of_birth) },
      { label: "Gender", ok: notEmpty(form.gender) },
      { label: "Telephone", ok: notEmpty(form.telephone) },
      { label: "Event", ok: notEmpty(form.event_name) },
      { label: "Triage center", ok: notEmpty(form.triage_center) },
      { label: "Patient contact time", ok: notEmpty(form.patient_contact_time) },
      { label: "Reason for treatment", ok: notEmpty(form.reason_for_treatment) },
      { label: "Triage tag color", ok: notEmpty(form.triage_tag_color) },
      { label: "Allergies (or NKA / Unable to Obtain)", ok: !!(notEmpty(form.allergies) || form.allergies_flags.nka || form.allergies_flags.unable) },
      { label: "Current medications (or None / Unable to Obtain)", ok: !!(notEmpty(form.current_medication) || form.meds_flags.none || form.meds_flags.unable) },
      { label: "Past medical history (at least one)", ok: (form.past_medical_history || []).length > 0 },
      { label: "Vital signs (at least one set with Pulse or BP, or N/A)", ok: !!(form.vitals_na || form.vitals.some((v) => notEmpty(v.pulse) || notEmpty(v.bp))) },
      { label: "Patient Assessment, Findings and Treatments (min 40 characters)", ok: (form.physical_assessment || "").trim().length >= 40 },
      { label: "HIPAA signature status", ok: notEmpty(form.hipaa_signature_status) },
      { label: "Provider name", ok: notEmpty(form.provider_name) },
      { label: "Provider signature", ok: notEmpty(form.provider_signature_data_url) },
    ];
  }, [form]);
  const doneCount = checklist.filter((c) => c.ok).length;
  const totalCount = checklist.length;
  const progress = Math.round((doneCount / totalCount) * 100);
  const missing = checklist.filter((c) => !c.ok);
  const canFinalize = missing.length === 0;
  const [showProgress, setShowProgress] = useState(false);

  function toggleArrayValue(field, value) {
    setForm((f) => ({
      ...f,
      [field]: f[field].includes(value) ? f[field].filter((v) => v !== value) : [...f[field], value],
    }));
  }

  async function persist(status) {
    if (busy) return; // guard against double-clicks that could double-post
    if (!form.first_name.trim() || !form.last_name.trim()) {
      toast.error("First and last name are required");
      return;
    }
    // Only "Submit & Close" (status=submitted) is gated by the full
    // 100% checklist — draft saves accept any state so a crew can keep
    // an in-progress record and come back to it.
    if (status === "submitted" && !canFinalize) {
      toast.error(
        `${missing.length} field${missing.length === 1 ? "" : "s"} still needed before you can close this report`,
        {
          action: { label: "Show missing", onClick: () => setShowProgress(true) },
          duration: 6000,
        }
      );
      setShowProgress(true);
      return;
    }
    if (form.form_type === "ACR" && (form.physical_assessment || "").length < 40) {
      toast.error("Patient Assessment, Findings and Treatments must be at least 40 characters");
      return;
    }
    // Flatten helper flags into narrative for backend simplicity
    const allergies = form.allergies_flags.nka
      ? "No Known Allergies"
      : form.allergies_flags.unable
        ? "Unable to Obtain"
        : form.allergies;
    const current_medication = form.meds_flags.none
      ? "No Current Medication"
      : form.meds_flags.unable
        ? "Unable to Obtain"
        : form.current_medication;
    const payload = {
      form_type: form.form_type,
      first_name: form.first_name, last_name: form.last_name, date_of_birth: form.date_of_birth || null,
      gender: form.gender || null, address: form.address || null, telephone: form.telephone || null,
      form_date: form.form_date || null, event_id: form.event_id || null, event_name: form.event_name || null,
      triage_center: form.triage_center || null, patient_contact_time: form.patient_contact_time || null,
      reason_for_treatment: form.reason_for_treatment || null,
      triage_tag_color: form.triage_tag_color || null,
      allergies, current_medication,
      past_medical_history: form.past_medical_history,
      other_medical_history: form.other_medical_history || null,
      vitals: form.vitals, vitals_na: form.vitals_na,
      physical_assessment: form.physical_assessment,
      being_transported: form.being_transported,
      transported_by: form.being_transported === true ? (form.transported_by || null) : null,
      hipaa_signature_status: form.hipaa_signature_status || null,
      hipaa_signature_date: form.hipaa_signature_date || null,
      hipaa_signature_data_url: form.hipaa_signature_data_url || null,
      rma_signature_status: form.rma_signature_status || null,
      rma_signature_date: form.rma_signature_date || null,
      rma_signature_data_url: form.rma_signature_data_url || null,
      provider_name: form.provider_name || null,
      provider_certification: form.provider_certification || null,
      provider_signature_data_url: form.provider_signature_data_url || null,
      otc_meds: form.otc_meds || null,
      otc_notes: form.otc_notes || null,
      otc_disclaimer_signature_data_url: form.otc_disclaimer_signature_data_url || null,
      otc_disclaimer_signature_date: form.otc_disclaimer_signature_date || null,
      status,
    };
    setBusy(true);
    try {
      const { data } = isEdit
        ? await api.patch(`/triage/patients/${patientId}`, payload)
        : await api.post("/triage/patients", payload);
      toast.success(
        status === "draft"
          ? (isEdit ? "Draft updated" : "Draft saved")
          : (isEdit ? "Patient record updated" : "Patient record submitted")
      );
      if (status === "submitted") nav("/triage/patients", { replace: true });
      else setForm((f) => ({ ...f, status: data.status }));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  }

  return (
    <TriageLayout showBack>
      <div className="max-w-3xl mx-auto px-4 py-6">
        {isEdit ? (
          <div className="bg-amber-50 border border-amber-200 rounded-sm px-3 py-2 mb-4 flex items-center justify-between gap-3" data-testid="tri-edit-banner">
            <div>
              <div className="tab-tag text-amber-800">
                Editing Existing Record{form.pcr_number ? ` · PCR # ${form.pcr_number}` : ""}
              </div>
              <div className="text-sm text-amber-900 font-medium">
                {form.first_name} {form.last_name} · {form.status?.toUpperCase() || "DRAFT"}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="text-[11px] text-amber-700 mono">ID: {patientId?.slice(0, 8)}</div>
              {canDelete && (
                <Button variant="outline" size="sm" className="rounded-sm h-7 px-2 text-red-600 border-red-200 hover:bg-red-50" data-testid="tri-delete-record"
                        onClick={async () => {
                          const label = `${form.first_name || ""} ${form.last_name || ""}`.trim() || "this record";
                          if (!window.confirm(`Delete triage record for ${label}?`)) return;
                          try {
                            await api.delete(`/triage/patients/${patientId}`);
                            const pid = patientId;
                            nav("/triage/patients", { replace: true });
                            // 5s Undo toast — restore endpoint clears the soft-delete marker.
                            toast(`Deleted "${label}"`, {
                              description: "This record was moved to the trash. Tap Undo to restore.",
                              duration: 5000,
                              action: {
                                label: "Undo",
                                onClick: async () => {
                                  try {
                                    await api.post(`/triage/patients/${pid}/restore`);
                                    toast.success("Record restored");
                                  } catch (err) {
                                    toast.error(err.response?.data?.detail || "Restore failed");
                                  }
                                },
                              },
                            });
                          } catch (err) {
                            toast.error(err.response?.data?.detail || "Delete failed");
                          }
                        }}>
                  <Trash size={12} weight="bold" className="mr-1" /> Delete
                </Button>
              )}
            </div>
          </div>
        ) : (
          <div className="bg-sky-50 border border-sky-200 rounded-sm px-3 py-2 mb-4 flex items-center justify-between" data-testid="tri-new-banner">
            <div className="tab-tag text-sky-800">New Patient Care Report</div>
            <div className="text-[11px] text-sky-700 mono">PCR # will be assigned automatically on save</div>
          </div>
        )}
        {loading ? (
          <div className="bg-white surface-grid p-6 text-center text-slate-500">Loading record…</div>
        ) : (<>
        {/* Form-type toggle */}
        <div className="bg-white surface-grid p-3 mb-4">
          <div className="tab-tag text-slate-500 mb-2">Form Type</div>
          <div className="flex gap-2">
            {["ACR (Patient Care Report)", "OTC Medication"].map((label, i) => {
              const val = i === 0 ? "ACR" : "OTC";
              return (
                <button key={val} type="button" onClick={() => setForm({ ...form, form_type: val })} data-testid={`tri-form-type-${val}`}
                  className={`px-3 py-2 rounded-sm text-sm font-semibold ${form.form_type === val ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}>
                  {label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Progress — click to expand a checklist showing what's still
            missing before the record can be closed out. */}
        <div className="bg-white surface-grid p-3 mb-4" data-testid="tri-progress-card">
          <button
            type="button"
            className="w-full text-left"
            onClick={() => setShowProgress((v) => !v)}
            data-testid="tri-progress-toggle"
            aria-expanded={showProgress}
            title={showProgress ? "Hide checklist" : "Show missing fields"}
          >
            <div className="flex items-center justify-between text-xs mb-1">
              <span className={progress < 100 ? "text-red-600 font-semibold" : "text-emerald-700 font-semibold"}>
                {progress}% Complete
              </span>
              <span className="text-slate-500">
                {missing.length === 0 ? "All fields complete" : `${missing.length} of ${totalCount} field${missing.length === 1 ? "" : "s"} remaining`}
                <span className="ml-2 text-slate-400">{showProgress ? "▲" : "▼"}</span>
              </span>
            </div>
            <div className="h-1 bg-slate-100 rounded-sm overflow-hidden">
              <div className={`h-full transition-all ${progress < 100 ? "bg-red-500" : "bg-emerald-500"}`} style={{ width: `${progress}%` }} />
            </div>
          </button>
          {showProgress && (
            <ul className="mt-3 border-t border-slate-100 pt-3 space-y-1 text-xs" data-testid="tri-progress-list">
              {checklist.map((c) => (
                <li key={c.label} className="flex items-start gap-2">
                  <span className={`mt-0.5 inline-block w-3 h-3 rounded-sm flex-none ${c.ok ? "bg-emerald-500" : "bg-red-500"}`} aria-hidden />
                  <span className={c.ok ? "text-slate-500 line-through" : "text-slate-700"}>{c.label}</span>
                </li>
              ))}
              {missing.length > 0 && (
                <li className="mt-2 text-[11px] text-slate-500 italic">
                  You can still <b>Save (Keep Open)</b> to preserve a draft, but <b>Submit &amp; Close</b> requires 100%.
                </li>
              )}
            </ul>
          )}
        </div>

        <Section icon={<UserPlus size={18} weight="bold" />} title="Patient Information">
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="First Name *"><Input required value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} className="rounded-sm" data-testid="tri-first-name" /></Field>
            <Field label="Last Name *"><Input required value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} className="rounded-sm" data-testid="tri-last-name" /></Field>
            <Field label="Date of Birth"><Input type="date" value={form.date_of_birth} onChange={(e) => setForm({ ...form, date_of_birth: e.target.value })} className="rounded-sm" data-testid="tri-dob" /></Field>
            <Field label="Gender">
              <select value={form.gender} onChange={(e) => setForm({ ...form, gender: e.target.value })} className="w-full h-10 rounded-sm border border-slate-200 px-2 text-sm bg-white" data-testid="tri-gender">
                <option value="">Select Gender</option><option>Male</option><option>Female</option><option>X</option>
              </select>
            </Field>
            <Field label="Address"><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Street Address, City, State, ZIP" className="rounded-sm" /></Field>
            <Field label="Telephone"><Input value={form.telephone} onChange={(e) => setForm({ ...form, telephone: e.target.value })} placeholder="(XXX) XXX-XXXX" className="rounded-sm" /></Field>
          </div>
        </Section>

        {form.form_type === "ACR" && (
        <Section icon={<ClipboardText size={18} weight="bold" />} title="Event & Assessment Details">
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Form Date"><Input type="date" value={form.form_date} onChange={(e) => setForm({ ...form, form_date: e.target.value })} className="rounded-sm" /></Field>
            <Field label="Event">
              <select value={form.event_id} onChange={(e) => {
                const ev = events.find((x) => x.id === e.target.value);
                const centers = ev?.triage_centers || [];
                setForm({
                  ...form,
                  event_id: e.target.value,
                  event_name: ev?.name || "",
                  // Auto-select the only center if there's exactly one; else
                  // clear so the operator picks explicitly.
                  triage_center: centers.length === 1 ? centers[0] : "",
                });
              }} className="w-full h-10 rounded-sm border border-slate-200 px-2 text-sm bg-white" data-testid="tri-event">
                <option value="">Select event…</option>
                {events.map((ev) => (<option key={ev.id} value={ev.id}>{ev.name}{ev.event_date ? ` — ${ev.event_date}` : ""}{ev.is_past ? " (past)" : ""}</option>))}
              </select>
            </Field>
            <Field label="Triage Center">
              {selectedEvent && (selectedEvent.triage_centers || []).length > 0 ? (
                <>
                  <select value={form.triage_center} onChange={(e) => setForm({ ...form, triage_center: e.target.value })} className="w-full h-10 rounded-sm border border-slate-200 px-2 text-sm bg-white" data-testid="tri-center">
                    <option value="">Select center…</option>
                    {selectedEvent.triage_centers.map((c) => (<option key={c} value={c}>{c}</option>))}
                  </select>
                  <div className="text-[10px] text-slate-500 mt-1">
                    {selectedEvent.triage_centers.length} center{selectedEvent.triage_centers.length === 1 ? "" : "s"} on file for this event
                  </div>
                </>
              ) : (
                <Input value={form.triage_center} onChange={(e) => setForm({ ...form, triage_center: e.target.value })} className="rounded-sm" placeholder={form.event_id ? "No centers on file — type one" : "Pick an event first"} data-testid="tri-center" />
              )}
            </Field>
            <Field label="Patient Contact Time (ET, 24-hr)"><MilTimeInput value={form.patient_contact_time} onChange={(v) => setForm({ ...form, patient_contact_time: v })} data-testid="tri-contact-time" /></Field>
            <Field label="Reason for Treatment">
              <select value={form.reason_for_treatment} onChange={(e) => setForm({ ...form, reason_for_treatment: e.target.value })} className="w-full h-10 rounded-sm border border-slate-200 px-2 text-sm bg-white" data-testid="tri-reason">
                <option value="">Select reason</option>{REASONS.map((r) => <option key={r}>{r}</option>)}
              </select>
            </Field>
          </div>
          <div className="mt-3">
            <div className="tab-tag text-slate-500 mb-1">Triage Tag Color</div>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2" data-testid="tri-tag-grid">
              {TAG_COLORS.map((t) => (
                <button key={t.key} type="button" onClick={() => setForm({ ...form, triage_tag_color: t.key })} data-testid={`tri-tag-${t.key}`}
                  className={`${t.bg} text-white text-xs font-bold py-3 rounded-sm ${form.triage_tag_color === t.key ? "ring-2 ring-offset-2 ring-slate-900" : "opacity-90 hover:opacity-100"}`}>
                  {t.label}
                </button>
              ))}
            </div>
          </div>
        </Section>
        )}

        {/* Medical History: the OTC path only surfaces the Allergies
            block — Current Meds / PMH / Other MH are ACR-only since
            those inform decisions the OTC form isn't making. Same
            component reuse via FlagField keeps the UX consistent. */}
        <Section icon={<HeartStraight size={18} weight="bold" />} title={form.form_type === "OTC" ? "Allergies" : "Medical History"}>
          <FlagField label="Allergies *" value={form.allergies}
            onChange={(v) => setForm({ ...form, allergies: v })}
            flags={form.allergies_flags} setFlags={(fl) => setForm({ ...form, allergies_flags: fl })}
            flagLabels={{ nka: "No Known Allergies", unable: "Unable to Obtain" }}
            testid="tri-allergies" />
          {form.form_type === "ACR" && (
            <>
              <FlagField label="Current Medication *" value={form.current_medication}
                onChange={(v) => setForm({ ...form, current_medication: v })}
                flags={form.meds_flags} setFlags={(fl) => setForm({ ...form, meds_flags: fl })}
                flagLabels={{ none: "No Current Medication", unable: "Unable to Obtain" }}
                testid="tri-meds" />
              <div className="mt-3">
                <div className="tab-tag text-slate-500 mb-1">Past Medical History (PMHX) *</div>
                <div className="grid sm:grid-cols-3 gap-2" data-testid="tri-pmh-grid">
                  {PMH_CHECKS.map((v) => (
                    <label key={v} className="flex items-center gap-2 text-sm bg-white border border-slate-200 rounded-sm px-2 py-1.5">
                      <Checkbox checked={form.past_medical_history.includes(v)} onCheckedChange={() => toggleArrayValue("past_medical_history", v)} />
                      <span>{v}</span>
                    </label>
                  ))}
                </div>
              </div>
              <Field label="Other Medical History" className="mt-3">
                <Textarea value={form.other_medical_history} onChange={(e) => setForm({ ...form, other_medical_history: e.target.value })} className="rounded-sm" rows={2} />
              </Field>
            </>
          )}
        </Section>

        {form.form_type === "OTC" && (
          <Section icon={<HeartStraight size={18} weight="bold" />} title="Medication Administered *">
            <div className="text-[11px] text-slate-500 mb-2">
              Tick every OTC medication given. Multiple selections are allowed.
            </div>
            <div className="grid sm:grid-cols-2 gap-2" data-testid="tri-otc-meds-grid">
              {[
                { key: "tylenol", brand: "Tylenol", generic: "acetaminophen" },
                { key: "motrin", brand: "Motrin", generic: "ibuprofen" },
                { key: "benadryl", brand: "Benadryl", generic: "diphenhydramine" },
                { key: "antacids", brand: "Anti-Acids", generic: "calcium carbonate / famotidine" },
              ].map((m) => {
                const on = !!(form.otc_meds && form.otc_meds[m.key]);
                return (
                  <label key={m.key} className={`flex items-center gap-3 border rounded-sm px-3 py-2 cursor-pointer ${on ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"}`}>
                    <Checkbox
                      checked={on}
                      onCheckedChange={(next) => setForm((prev) => ({
                        ...prev,
                        otc_meds: { ...(prev.otc_meds || {}), [m.key]: !!next },
                      }))}
                      data-testid={`tri-otc-${m.key}`}
                    />
                    <div className="leading-tight">
                      <div className="text-sm font-semibold">{m.brand}</div>
                      <div className={`text-[11px] ${on ? "text-slate-300" : "text-slate-500"} italic`}>{m.generic}</div>
                    </div>
                  </label>
                );
              })}
            </div>
            <Field label="Additional details (dose, route, time, other meds…)" className="mt-3">
              <Textarea
                value={form.otc_notes}
                onChange={(e) => setForm({ ...form, otc_notes: e.target.value })}
                className="rounded-sm"
                rows={3}
                placeholder="e.g. Tylenol 500mg PO at 14:20 for HA; patient tolerated well"
                data-testid="tri-otc-notes"
              />
            </Field>
          </Section>
        )}

        {form.form_type === "OTC" && (
          <Section title="OTC Medication Disclaimer & Acknowledgment (Patient Signature)">
            <div className="text-[11px] text-slate-700 mb-3 max-h-56 overflow-y-auto border border-slate-200 rounded-sm p-3 bg-slate-50 leading-relaxed" data-testid="tri-otc-disclaimer-text">
              <div className="font-semibold text-sm text-slate-900">Bedford Stuyvesant Volunteer Ambulance Corps</div>
              <div className="font-semibold text-slate-900 mb-2">Over-the-Counter Medication Disclaimer and Acknowledgment</div>
              <p className="mb-2">I acknowledge that any over-the-counter (&ldquo;OTC&rdquo;) medication made available to me by the Bedford Stuyvesant Volunteer Ambulance Corps (&ldquo;BSVAC&rdquo;) is being provided for my voluntary use only.</p>
              <p className="mb-2">I understand that BSVAC personnel are not prescribing, administering, or directing me to take the medication. Any decision to take an OTC medication provided by BSVAC is made voluntarily by me, and I am responsible for following the manufacturer&rsquo;s instructions, warnings, dosage recommendations, and labeling associated with the medication.</p>
              <p className="mb-1 font-semibold">By signing below, I acknowledge and confirm that:</p>
              <ul className="list-disc pl-5 space-y-1 mb-2">
                <li>I have disclosed and documented, to the best of my knowledge, all known allergies and sensitivities to medications, foods, and other substances.</li>
                <li>I have informed BSVAC personnel of any known allergy or previous adverse reaction that may be relevant to the medication I am requesting.</li>
                <li>I understand that it is my responsibility to determine whether I can safely take the medication provided.</li>
                <li>I understand that BSVAC makes no representation or guarantee regarding the effectiveness of the medication or whether it is appropriate for my individual medical condition.</li>
                <li>I voluntarily accept all risks associated with taking the medication, including the possibility of an allergic reaction, medication interaction, side effect, adverse reaction, worsening of symptoms, or other medical complication.</li>
                <li>I understand that BSVAC, its officers, members, volunteers, employees, agents, and representatives shall not be held responsible or liable, to the fullest extent permitted by law, for any injury, illness, allergic reaction, adverse effect, complication, or other consequence arising from or related to my voluntary use of the OTC medication provided.</li>
                <li>I understand that receiving an OTC medication from BSVAC is not a substitute for evaluation, diagnosis, or treatment by a physician or other qualified healthcare provider. If my symptoms persist, worsen, or I experience an unexpected reaction, I understand that I should seek appropriate medical care.</li>
              </ul>
              <p>By signing below, I confirm that I have read and understand this acknowledgment and voluntarily accept the OTC medication being provided.</p>
            </div>
            <Field label="Signature Date">
              <Input
                type="date"
                value={form.otc_disclaimer_signature_date}
                onChange={(e) => setForm({ ...form, otc_disclaimer_signature_date: e.target.value })}
                className="rounded-sm max-w-xs"
                data-testid="tri-otc-disclaimer-date"
              />
            </Field>
            <SignaturePad
              testid="tri-otc-disclaimer-signature-pad"
              value={form.otc_disclaimer_signature_data_url}
              onChange={(dataUrl) => setForm((prev) => ({ ...prev, otc_disclaimer_signature_data_url: dataUrl }))}
            />
          </Section>
        )}

        {form.form_type === "ACR" && (
        <>
        <Section icon={<HeartStraight size={18} weight="bold" />} title="Vital Signs *">
          <label className="flex items-center gap-2 text-sm mb-3">
            <Checkbox checked={form.vitals_na} onCheckedChange={(v) => setForm({ ...form, vitals_na: !!v })} data-testid="tri-vitals-na" />
            <span>N/A — No vitals taken</span>
          </label>
          {!form.vitals_na && form.vitals.map((v, i) => (
            <div key={i} className="border border-slate-200 rounded-sm p-3 mb-2">
              <div className="flex items-center justify-between mb-2">
                <div className="tab-tag text-slate-500">Vital Signs — Set {i + 1}</div>
                {i > 0 && (
                  <button type="button" onClick={() => copyPreviousVital(i)}
                          className="text-[11px] font-semibold text-slate-600 hover:text-slate-900 bg-slate-100 hover:bg-slate-200 rounded-sm px-2 py-0.5 transition-colors"
                          data-testid={`tri-vitals-copy-prev-${i}`}
                          title={`Copy Set ${i} into Set ${i + 1}`}>
                    Copy from Set {i}
                  </button>
                )}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-6 gap-2">
                <FieldSmall label="Time (ET, 24-hr)"><MilTimeInput value={v.time} onChange={(val) => updateVital(i, "time", val)} data-testid={`tri-vitals-time-${i}`} /></FieldSmall>
                <FieldSmall label="Pulse"><Input value={v.pulse} onChange={(e) => updateVital(i, "pulse", e.target.value)} className="rounded-sm" /></FieldSmall>
                <FieldSmall label="BP"><Input value={v.bp} onChange={(e) => updateVital(i, "bp", e.target.value)} className="rounded-sm" placeholder="120/80" /></FieldSmall>
                <FieldSmall label="Resp"><Input value={v.respiration} onChange={(e) => updateVital(i, "respiration", e.target.value)} className="rounded-sm" /></FieldSmall>
                <FieldSmall label="SpO₂"><Input value={v.o2} onChange={(e) => updateVital(i, "o2", e.target.value)} className="rounded-sm" /></FieldSmall>
                <FieldSmall label="BGL"><Input value={v.bgl || ""} onChange={(e) => updateVital(i, "bgl", e.target.value)} className="rounded-sm" placeholder="mg/dL" data-testid={`tri-vitals-bgl-${i}`} /></FieldSmall>
              </div>
              <div className="mt-2">
                <Label className="tab-tag text-[9px]">CTC (multi-select)</Label>
                <div className="flex flex-wrap gap-1.5" data-testid={`tri-ctc-${i}`}>
                  {CTC_OPTIONS.map((o) => {
                    const active = Array.isArray(v.ctc) && v.ctc.includes(o);
                    return (
                      <button key={o} type="button"
                              onClick={() => {
                                const cur = Array.isArray(v.ctc) ? v.ctc : [];
                                updateVital(i, "ctc", cur.includes(o) ? cur.filter((x) => x !== o) : [...cur, o]);
                              }}
                              className={`text-xs rounded-sm px-3 py-1.5 border transition-colors ${active ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200 text-slate-700 hover:bg-slate-50"}`}
                              data-testid={`tri-ctc-${i}-${o.toLowerCase()}`}>
                        {o}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="mt-2">
                <Label className="tab-tag text-[9px]">AVPU (level of consciousness — single-select)</Label>
                <div className="flex flex-wrap gap-1.5" data-testid={`tri-avpu-${i}`}>
                  {[
                    ["A", "Alert"],
                    ["V", "Verbal"],
                    ["P", "Pain"],
                    ["U", "Unresponsive"],
                  ].map(([code, label]) => {
                    const active = v.avpu === code;
                    return (
                      <button key={code} type="button"
                              onClick={() => updateVital(i, "avpu", active ? "" : code)}
                              className={`text-xs rounded-sm px-3 py-1.5 border transition-colors ${active ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200 text-slate-700 hover:bg-slate-50"}`}
                              title={`${code} — ${label}`}
                              data-testid={`tri-avpu-${i}-${code.toLowerCase()}`}>
                        <span className="font-semibold">{code}</span> — {label}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          ))}
        </Section>

        <Section icon={<HeartStraight size={18} weight="bold" />} title="Patient Assessment, Findings and Treatments *">
          <Textarea value={form.physical_assessment} onChange={(e) => setForm({ ...form, physical_assessment: e.target.value })} rows={4} className="rounded-sm" data-testid="tri-assessment" />
          <div className={`text-[11px] mt-1 ${(form.physical_assessment || "").length >= 40 ? "text-emerald-700" : "text-red-600"}`}>
            {(form.physical_assessment || "").length}/40 characters minimum
          </div>
        </Section>

        <Section icon={<ArrowsClockwise size={18} weight="bold" />} title="Transport Information">
          <div className="flex gap-2">
            {[["Yes", true], ["No", false]].map(([label, val]) => (
              <button key={label} type="button" onClick={() => setForm({ ...form, being_transported: val })} data-testid={`tri-transport-${label.toLowerCase()}`}
                className={`px-4 py-2 rounded-sm text-sm font-semibold ${form.being_transported === val ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-700 hover:bg-slate-50"}`}>
                {label}
              </button>
            ))}
          </div>
          {form.being_transported === true && (
            <div className="mt-3">
              <Label className="tab-tag">Transported By *</Label>
              <Input
                value={form.transported_by || ""}
                onChange={(e) => setForm({ ...form, transported_by: e.target.value })}
                placeholder="e.g. FDNY EMS Truck 22, BSVAC 1-Adam, Private Ambulance"
                className="rounded-sm"
                data-testid="tri-transported-by"
              />
              <div className="text-[10px] text-slate-500 mt-1">Which unit / agency took the patient?</div>
            </div>
          )}
        </Section>

        <Section title="HIPAA Privacy Authorization & Disclaimer *">
          <p className="text-[11px] text-slate-600 mb-2 max-h-32 overflow-y-auto border border-slate-200 rounded-sm p-2 bg-slate-50">
            I understand that the information I provide on this form may include protected health information (PHI) as defined by HIPAA. This information will be used and disclosed solely for the purposes of treatment, payment, and healthcare operations, as permitted by law. I acknowledge that my health information may be shared with authorized personnel and affiliated organizations. I understand that I have the right to request restrictions, inspect my records, and revoke this authorization in writing at any time. By signing below, I consent to the use and disclosure of my PHI as described above.
          </p>
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Signature Status">
              <select value={form.hipaa_signature_status} onChange={(e) => setForm({ ...form, hipaa_signature_status: e.target.value })} className="w-full h-10 rounded-sm border border-slate-200 px-2 text-sm bg-white" data-testid="tri-hipaa-status">
                <option value="">Select signature status</option>{SIG_STATUS.map((o) => <option key={o}>{o}</option>)}
              </select>
            </Field>
            <Field label="Date"><Input type="date" value={form.hipaa_signature_date} onChange={(e) => setForm({ ...form, hipaa_signature_date: e.target.value })} className="rounded-sm" /></Field>
          </div>
          <SignaturePad
            testid="tri-hipaa-signature-pad"
            value={form.hipaa_signature_data_url}
            onChange={(dataUrl) => setForm((prev) => ({ ...prev, hipaa_signature_data_url: dataUrl }))}
          />
        </Section>

        <Section title="Treat and Release Disclaimer">
          <p className="text-[11px] text-slate-600 mb-2 max-h-32 overflow-y-auto border border-slate-200 rounded-sm p-2 bg-slate-50">
            I acknowledge that I have been evaluated and treated on-scene by emergency medical personnel and, following that treatment, elect to remain on-scene rather than be transported to a hospital by ambulance. I understand that even after treatment my condition may worsen, and that refusing transport may place my health at risk of injury, illness, permanent disability, or death. I have been advised to seek follow-up care with my personal physician or return to an emergency department if any new or worsening symptoms develop, and I understand that I may call 911 again at any time. I affirm that I am of sound mind and legally able to make this decision voluntarily, and I release the Bedford Stuyvesant Volunteer Ambulance Corps and its members from any liability arising from my choice to be treated and released. I have read and fully understand this disclaimer.
          </p>
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Signature Status">
              <select value={form.rma_signature_status} onChange={(e) => setForm({ ...form, rma_signature_status: e.target.value })} className="w-full h-10 rounded-sm border border-slate-200 px-2 text-sm bg-white" data-testid="tri-tr-status">
                <option value="">Select signature status</option>{SIG_STATUS.map((o) => <option key={o}>{o}</option>)}
              </select>
            </Field>
            <Field label="Date"><Input type="date" value={form.rma_signature_date} onChange={(e) => setForm({ ...form, rma_signature_date: e.target.value })} className="rounded-sm" /></Field>
          </div>
          <SignaturePad
            testid="tri-tr-signature-pad"
            value={form.rma_signature_data_url}
            onChange={(dataUrl) => setForm((prev) => ({ ...prev, rma_signature_data_url: dataUrl }))}
          />
        </Section>
        </>
        )}

        <Section title="Provider Signature">
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Provider Name"><Input value={form.provider_name} onChange={(e) => setForm({ ...form, provider_name: e.target.value })} className="rounded-sm" data-testid="tri-provider-name" /></Field>
            <Field label="Level of Certification"><Input value={form.provider_certification} onChange={(e) => setForm({ ...form, provider_certification: e.target.value })} className="rounded-sm" placeholder="EMT / Paramedic / Driver" /></Field>
          </div>
          <SignaturePad
            testid="tri-signature-pad"
            value={form.provider_signature_data_url}
            onChange={(dataUrl) => setForm((prev) => ({ ...prev, provider_signature_data_url: dataUrl }))}
          />
        </Section>

        <div className="flex items-center gap-2 justify-end sticky bottom-0 bg-slate-50 py-3 border-t border-slate-200">
          <Button variant="outline" className="rounded-sm" onClick={() => nav(isEdit ? "/triage/patients" : "/triage")} data-testid="tri-cancel">Cancel</Button>
          <Button variant="outline" className="rounded-sm" disabled={busy} onClick={() => persist("draft")} data-testid="tri-save-draft">
            <FloppyDisk size={14} weight="bold" className="mr-1" /> {isEdit ? "Update (Keep Open)" : "Save (Keep Open)"}
          </Button>
          <Button className="rounded-sm bg-slate-900 hover:bg-slate-800 text-white" disabled={busy} onClick={() => persist("submitted")} data-testid="tri-submit">
            {isEdit ? "Update & Close" : "Submit & Close"}
          </Button>
        </div>
        </>)}
      </div>
    </TriageLayout>
  );

  function updateVital(i, key, val) {
    setForm((f) => {
      const next = [...f.vitals];
      next[i] = { ...next[i], [key]: val };
      return { ...f, vitals: next };
    });
  }

  // Duplicate the previous vital set into slot i. Keeps its own Time blank so
  // providers don't accidentally submit two sets with the same timestamp.
  function copyPreviousVital(i) {
    setForm((f) => {
      if (i <= 0) return f;
      const prev = f.vitals[i - 1] || {};
      const next = [...f.vitals];
      next[i] = {
        ...next[i],
        pulse: prev.pulse || "",
        bp: prev.bp || "",
        respiration: prev.respiration || "",
        o2: prev.o2 || "",
        ctc: Array.isArray(prev.ctc) ? [...prev.ctc] : (prev.ctc ? [prev.ctc] : []),
        bgl: prev.bgl || "",
        time: "", // force provider to enter a fresh time
      };
      return { ...f, vitals: next };
    });
  }
}

// --- helpers -----------------------------------------------------------------

function Section({ title, icon, children }) {
  return (
    <div className="bg-white surface-grid p-4 mb-4">
      <div className="flex items-center gap-2 mb-3">
        {icon && <span className="text-slate-500">{icon}</span>}
        <div className="font-display font-bold text-slate-900">{title}</div>
      </div>
      {children}
    </div>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <div className={className}>
      <Label className="tab-tag">{label}</Label>
      {children}
    </div>
  );
}

function FieldSmall({ label, children }) {
  return (
    <div>
      <Label className="tab-tag text-[9px]">{label}</Label>
      {children}
    </div>
  );
}

/**
 * 24-hour military time input.
 *
 * Uses `type="text"` (rather than `type="time"`) so the operator ALWAYS
 * sees `HH:MM` in 24-hour format regardless of the device / OS clock
 * preference — browsers render `type="time"` in 12-hour if the OS is
 * set to 12-hour. Storage is unchanged (still an "HH:MM" string).
 *
 * Behaviour:
 * - `inputMode="numeric"` triggers the numeric keypad on iOS/Android
 * - Auto-inserts a colon after the second digit for smooth typing
 * - `pattern` restricts to 00:00–23:59 for HTML form validation
 * - Blur normalises "9:5" → "09:05"
 */
function MilTimeInput({ value, onChange, "data-testid": testId, className = "" }) {
  function handleChange(raw) {
    // Strip anything except digits and colon; auto-insert colon after 2
    // digits so an operator can type "1430" and see "14:30".
    let s = String(raw || "").replace(/[^\d:]/g, "").slice(0, 5);
    if (s.length >= 3 && s.indexOf(":") === -1) {
      s = s.slice(0, 2) + ":" + s.slice(2, 4);
    }
    onChange(s);
  }
  function handleBlur() {
    const m = /^(\d{1,2}):?(\d{0,2})$/.exec(value || "");
    if (!m) return;
    const h = Math.min(23, parseInt(m[1] || "0", 10));
    const mn = Math.min(59, parseInt(m[2] || "0", 10));
    onChange(`${String(h).padStart(2, "0")}:${String(mn).padStart(2, "0")}`);
  }
  return (
    <Input
      type="text"
      inputMode="numeric"
      pattern="^([01]\\d|2[0-3]):[0-5]\\d$"
      maxLength={5}
      placeholder="HH:MM"
      value={value || ""}
      onChange={(e) => handleChange(e.target.value)}
      onBlur={handleBlur}
      className={`rounded-sm font-mono ${className}`}
      data-testid={testId}
    />
  );
}

function FlagField({ label, value, onChange, flags, setFlags, flagLabels, testid }) {
  return (
    <div className="mt-3">
      <div className="tab-tag text-slate-500 mb-1">{label}</div>
      <div className="flex flex-wrap gap-2 mb-2">
        {Object.entries(flagLabels).map(([k, l]) => (
          <label key={k} className="flex items-center gap-2 text-xs bg-slate-50 border border-slate-200 rounded-sm px-2 py-1">
            <Checkbox checked={!!flags[k]} onCheckedChange={(v) => setFlags({ ...flags, [k]: !!v })} />
            <span>{l}</span>
          </label>
        ))}
      </div>
      <Input value={value} onChange={(e) => onChange(e.target.value)} disabled={flags.nka || flags.unable || flags.none} className="rounded-sm" placeholder="Type here…" data-testid={testid} />
    </div>
  );
}

/**
 * Minimal pointer-driven signature pad. Draws to a 500x120 canvas, exports
 * as PNG data URL. Clear button resets both the canvas and the stored value.
 */
function SignaturePad({ value, onChange, testid = "tri-signature-pad" }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const painted = useRef(false);

  // When editing, paint the saved signature image back onto the canvas
  // once — after that any new stroke replaces it. useEffect so the canvas
  // node is mounted before we try to draw.
  React.useEffect(() => {
    if (!value || painted.current) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const img = new Image();
    img.onload = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      painted.current = true;
    };
    img.src = value;
  }, [value]);

  // Attach touch listeners natively with { passive: false } so we can call
  // preventDefault(). React's synthetic touch events are passive by
  // default, which caused iPad Safari to interpret drag-to-sign as a
  // swipe-back gesture and yank the operator away from the form.
  React.useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const onStart = (e) => { e.preventDefault(); drawing.current = true; drawAt(e, true); };
    const onMove  = (e) => { if (!drawing.current) return; e.preventDefault(); drawAt(e, false); };
    const onEnd   = (e) => { e.preventDefault(); finish(); };
    canvas.addEventListener("touchstart", onStart, { passive: false });
    canvas.addEventListener("touchmove",  onMove,  { passive: false });
    canvas.addEventListener("touchend",   onEnd,   { passive: false });
    canvas.addEventListener("touchcancel", onEnd,  { passive: false });
    return () => {
      canvas.removeEventListener("touchstart", onStart);
      canvas.removeEventListener("touchmove",  onMove);
      canvas.removeEventListener("touchend",   onEnd);
      canvas.removeEventListener("touchcancel", onEnd);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function drawAt(e, isStart) {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    // Support both touch and mouse events. Canvas has a fixed intrinsic
    // resolution of 500x120 but is rendered wider via CSS, so scale the
    // pointer coord into the internal coord space.
    const src = (e.touches && e.touches[0]) || (e.changedTouches && e.changedTouches[0]) || e;
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const x = (src.clientX - rect.left) * scaleX;
    const y = (src.clientY - rect.top) * scaleY;
    const ctx = canvas.getContext("2d");
    ctx.lineWidth = 2;
    ctx.strokeStyle = "#0f172a";
    ctx.lineCap = "round";
    if (isStart) { ctx.beginPath(); ctx.moveTo(x, y); }
    else { ctx.lineTo(x, y); ctx.stroke(); }
  }

  function finish() {
    drawing.current = false;
    if (canvasRef.current) onChange(canvasRef.current.toDataURL("image/png"));
  }

  // Mouse handlers stay via React (mouse events don't need preventDefault
  // to avoid swipe-back gestures on desktop).
  function mouseStart(e) { drawing.current = true; drawAt(e, true); }
  function mouseMove(e)  { if (drawing.current) drawAt(e, false); }
  function mouseEnd()    { if (drawing.current) finish(); }

  function clear() {
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext("2d");
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    }
    painted.current = false;
    onChange("");
  }

  return (
    <div className="mt-3">
      <Label className="tab-tag">Signature</Label>
      <canvas
        ref={canvasRef}
        width={500} height={120}
        // touch-action: none tells the browser this element handles its
        // own touches — no scrolling, no swipe-back, no pull-to-refresh.
        style={{ touchAction: "none" }}
        className="w-full h-32 border border-slate-300 rounded-sm bg-white cursor-crosshair select-none"
        onMouseDown={mouseStart} onMouseMove={mouseMove} onMouseUp={mouseEnd} onMouseLeave={mouseEnd}
        data-testid={testid}
      />
      <div className="flex items-center justify-between mt-2">
        <div className="text-[11px] text-slate-500">{value ? "Signature captured" : "Sign above using mouse or touch"}</div>
        <Button type="button" variant="outline" size="sm" className="rounded-sm" onClick={clear} data-testid={`${testid}-clear`}>
          <Eraser size={12} weight="bold" className="mr-1" /> Clear Signature
        </Button>
      </div>
    </div>
  );
}
